import { create } from 'zustand';

import { DepositMode, PaymentType, type Brand } from '@/models';

interface BrandState {
  brand: Brand | null;
  storeId: number | null;
  setBrand: (brand: Brand) => void;
  setStoreId: (storeId: number) => void;
  getBrandName: () => string;
  getBrandTerms: (language: 'en' | 'th') => string;
  getCancellationPolicy: (language: 'en' | 'th') => string;
  getShowTermsAndConditions: () => boolean;
  getShowCancellationPolicy: () => boolean;
  getBranchName: (providedStoreId?: number) => string;
  getMaxAdvanceBookingDays: () => number;
  getMaxCustomerCount: () => number;
  getPaymentType: () => PaymentType;
  getShowPackageUsage: () => boolean;
  getDepositMode: () => DepositMode;
  reset: () => void;
}

export const useBrandStore = create<BrandState>()((set, get) => ({
  brand: null,
  storeId: null,

  setBrand: (brand: Brand) => {
    set({ brand });
  },

  setStoreId: (storeId: number) => {
    set({ storeId });
  },

  getBrandName: () => {
    const { brand, storeId } = get();
    if (!brand) return '';

    if (storeId && brand.branches) {
      const store = brand.branches.find((branch) => branch.storeId === storeId);
      if (store?.name) return store.name;
    }
    return brand.name;
  },

  getBrandTerms: (language: 'en' | 'th') => {
    const { brand } = get();
    if (!brand?.termsAndConditions) return '';
    return (
      brand.termsAndConditions[language] || brand.termsAndConditions.en || ''
    );
  },

  getCancellationPolicy: (language: 'en' | 'th') => {
    const { brand } = get();
    if (!brand?.cancellationPolicy) return '';
    return (
      brand.cancellationPolicy[language] || brand.cancellationPolicy.en || ''
    );
  },

  getShowTermsAndConditions: () => {
    const { brand } = get();
    return brand?.showTermsAndConditions ?? true;
  },

  getShowCancellationPolicy: () => {
    const { brand } = get();
    return brand?.showCancellationPolicy ?? true;
  },

  getBranchName: (providedStoreId?: number) => {
    const { brand, storeId: brandStoreId } = get();
    const storeId = providedStoreId || brandStoreId;
    if (!brand?.branches || !storeId) return '';

    const store = brand.branches.find((branch) => branch.storeId === storeId);
    return store?.branchName || store?.name || '';
  },

  getMaxAdvanceBookingDays: () => {
    const { brand, storeId } = get();
    if (!brand?.branches || storeId === 0)
      return brand?.maxAdvanceBookingDays || 7;

    const store = brand.branches.find((branch) => branch.storeId === storeId);
    return store?.maxAdvanceBookingDays || brand?.maxAdvanceBookingDays || 7;
  },

  getMaxCustomerCount: () => {
    const { brand, storeId } = get();
    if (!brand?.branches || storeId === 0) return 10;

    const store = brand.branches.find((branch) => branch.storeId === storeId);
    return store?.maxCustomerCount || 10;
  },

  getPaymentType: () => {
    const { brand, storeId } = get();
    if (!brand?.branches || storeId === 0)
      return brand?.paymentType || PaymentType.None;

    const store = brand.branches.find((branch) => branch.storeId === storeId);
    return store?.paymentType || brand?.paymentType || PaymentType.None;
  },

  getShowPackageUsage: () => {
    const { brand, storeId } = get();
    if (!brand?.branches || storeId === 0) return true;

    const store = brand.branches.find((branch) => branch.storeId === storeId);
    return store?.showPackageUsage ?? true;
  },

  getDepositMode: () => {
    const { brand, storeId } = get();
    if (!brand?.branches || storeId === 0)
      return brand?.depositMode || DepositMode.Fixed;

    const store = brand.branches.find((branch) => branch.storeId === storeId);
    return store?.depositMode ?? DepositMode.Fixed;
  },

  reset: () => {
    set({ brand: null, storeId: null });
  },
}));
